import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  VocabularyImportJob,
  VocabularyImportResult,
  VocabularyImportRowsPage,
  VocabularyImportRowStatus,
} from 'app/types';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import HistoryPagination from 'components/HistoryPagination';
import { NotificationCenterContext } from 'components/NotificationCenter';
import PageIntro from 'components/PageIntro/PageIntro';
import TableCellContent from 'components/TableCellContent';
import { commonTableSx } from 'components/tableStyles';
import UIContainer from 'components/ui/UIContainer';
import { axiosInstance } from 'setup/axiosInstance';

const ROWS_PAGE_SIZE = 50;

const statusLabels: Record<VocabularyImportRowStatus, string> = {
  VALID_NEW: 'Буде додано',
  VALID_EXISTING: 'Вже існує',
  INVALID: 'Помилка',
  DUPLICATE_IN_FILE: 'Дублікат у файлі',
  IMPORTED: 'Додано',
  UPDATED: 'Оновлено',
  SKIPPED: 'Пропущено',
};

const getFilenameFromDisposition = (contentDisposition?: string) => {
  if (!contentDisposition) {
    return '';
  }

  const utfFilenameMatch = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)/,
  );
  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/);

  return decodeURIComponent(utfFilenameMatch?.[1] || filenameMatch?.[1] || '');
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const VocabularyImport: React.FC = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { pushNotification } = useContext(NotificationCenterContext);
  const [file, setFile] = useState<File | null>(null);
  const [job, setJob] = useState<VocabularyImportJob | null>(null);
  const [rowsPage, setRowsPage] = useState<VocabularyImportRowsPage | null>(
    null,
  );
  const [page, setPage] = useState(0);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [error, setError] = useState('');

  const isParsing = job?.status === 'PARSING' || job?.status === 'IMPORTING';
  const canUpload = !!file && !loading && !isParsing;
  const canConfirm =
    job?.status === 'VALIDATED' && (job.validRows || 0) > 0 && !confirmLoading;

  const summary = useMemo(
    () => [
      { label: 'Всього рядків', value: job?.totalRows || 0 },
      { label: 'Валідних', value: job?.validRows || 0 },
      { label: 'Існуючих', value: job?.existingRows || 0 },
      { label: 'Дублікатів', value: job?.duplicateRows || 0 },
      { label: 'Помилок', value: job?.invalidRows || 0 },
    ],
    [job],
  );

  useEffect(() => {
    if (!job || !isParsing) {
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      await fetchJob(job.id);
    }, 1200);

    return () => window.clearTimeout(timeoutId);
    // Poll is intentionally keyed off the job's id/status, not the whole
    // `job` object — depending on `job` would re-arm the timeout on every
    // poll response and change the polling cadence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParsing, job?.id, job?.status]);

  useEffect(() => {
    if (!job || job.status === 'PARSING') {
      return;
    }

    fetchRows(job.id, page);
    // Keyed off job id/status (not the whole `job` object) on purpose — see
    // the polling effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.status, page]);

  const fetchJob = async (jobId: number) => {
    const { data } = await axiosInstance.get<VocabularyImportJob>(
      `/vocabulary/import/jobs/${jobId}`,
    );
    setJob(data);
  };

  const fetchRows = async (jobId: number, nextPage: number) => {
    setRowsLoading(true);

    try {
      const { data } = await axiosInstance.get<VocabularyImportRowsPage>(
        `/vocabulary/import/jobs/${jobId}/rows`,
        {
          params: {
            page: nextPage,
            size: ROWS_PAGE_SIZE,
          },
        },
      );
      setRowsPage(data);
    } finally {
      setRowsLoading(false);
    }
  };

  const downloadTemplate = async () => {
    const response = await axiosInstance.get('/vocabulary/import/template', {
      responseType: 'blob',
    });
    const filename =
      getFilenameFromDisposition(response.headers['content-disposition']) ||
      'vocabulary-import-template.xlsx';

    downloadBlob(response.data, filename);
  };

  const createJob = async () => {
    if (!file) {
      return;
    }

    setLoading(true);
    setError('');
    setRowsPage(null);
    setPage(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axiosInstance.post<VocabularyImportJob>(
        '/vocabulary/import/jobs',
        formData,
      );
      setJob(data);
    } catch (e) {
      console.error(e);
      setError(
        'Не вдалося завантажити файл. Перевірте формат .xlsx і спробуйте ще раз.',
      );
    } finally {
      setLoading(false);
    }
  };

  const confirmImport = async () => {
    if (!job || !canConfirm) {
      return;
    }

    setConfirmLoading(true);
    setError('');

    try {
      const { data } = await axiosInstance.post<VocabularyImportResult>(
        `/vocabulary/import/jobs/${job.id}/confirm`,
        { updateExisting },
      );
      await fetchJob(job.id);
      await fetchRows(job.id, page);
      pushNotification(
        `Імпорт завершено: додано ${data.added}, оновлено ${data.updated}, пропущено ${data.skipped}`,
        'success',
        undefined,
        'center',
      );
    } catch (e) {
      console.error(e);
      setError('Не вдалося підтвердити імпорт. Спробуйте ще раз.');
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <UIContainer sx={{ mb: 8 }}>
      <BreadCrumbs />

      <PageIntro
        title={'Імпорт слів'}
        description={
          'Завантажте Excel-файл за шаблоном. Обов’язкове поле тільки Word; інші поля можна залишити порожніми.'
        }
      />

      <Stack spacing={3} sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Button
                  onClick={downloadTemplate}
                  startIcon={<DownloadIcon />}
                  variant={'outlined'}
                >
                  {'Завантажити шаблон'}
                </Button>

                <Button
                  onClick={() => inputRef.current?.click()}
                  startIcon={<UploadFileIcon />}
                  variant={'outlined'}
                >
                  {file ? file.name : 'Обрати .xlsx файл'}
                </Button>

                <input
                  accept={'.xlsx'}
                  hidden
                  ref={inputRef}
                  type={'file'}
                  onChange={(event) => {
                    setFile(event.target.files?.[0] || null);
                    setError('');
                  }}
                />

                <Button disabled={!canUpload} onClick={createJob}>
                  {loading || isParsing ? 'Перевірка...' : 'Перевірити файл'}
                </Button>
              </Stack>

              {error ? <Alert severity={'error'}>{error}</Alert> : null}

              {job ? (
                <Alert severity={job.status === 'FAILED' ? 'error' : 'info'}>
                  {job.status === 'PARSING'
                    ? 'Файл обробляється...'
                    : job.status === 'VALIDATED'
                    ? 'Файл перевірено. Перегляньте preview перед імпортом.'
                    : job.status === 'IMPORTING'
                    ? 'Імпорт виконується...'
                    : job.status === 'DONE'
                    ? 'Імпорт завершено.'
                    : job.errorMessage || 'Не вдалося обробити файл.'}
                </Alert>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        {job ? (
          <Card>
            <CardContent>
              <Stack spacing={3}>
                <Typography variant={'h3'}>{'Результат перевірки'}</Typography>

                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  sx={{ alignItems: { md: 'center' }, flexWrap: 'wrap' }}
                >
                  {summary.map(({ label, value }) => (
                    <Box
                      key={label}
                      sx={{
                        border: 1,
                        borderColor: 'primary.tableBorder',
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                      }}
                    >
                      <Typography color={'text.secondary'} variant={'body2'}>
                        {label}
                      </Typography>
                      <Typography fontWeight={700} variant={'subtitle2'}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                {job.existingRows > 0 ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={updateExisting}
                        onChange={(event) =>
                          setUpdateExisting(event.target.checked)
                        }
                      />
                    }
                    label={'Оновлювати існуючі слова'}
                  />
                ) : null}

                <Box>
                  <Button
                    disabled={!canConfirm}
                    onClick={confirmImport}
                    startIcon={<CheckIcon />}
                  >
                    {confirmLoading ? 'Імпорт...' : 'Підтвердити імпорт'}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        {rowsPage && rowsPage.content.length > 0 ? (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant={'h3'}>{'Preview рядків'}</Typography>

                <TableContainer>
                  <Table sx={commonTableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 80 }}>{'Row'}</TableCell>
                        <TableCell sx={{ width: 180 }}>{'Word'}</TableCell>
                        <TableCell sx={{ width: 170 }}>
                          {'Part of speech'}
                        </TableCell>
                        <TableCell sx={{ width: 220 }}>
                          {'Translation'}
                        </TableCell>
                        <TableCell>{'Definition'}</TableCell>
                        <TableCell sx={{ width: 120 }}>{'Learned'}</TableCell>
                        <TableCell sx={{ width: 150 }}>
                          {'Active speech'}
                        </TableCell>
                        <TableCell sx={{ width: 170 }}>{'Status'}</TableCell>
                        <TableCell sx={{ width: 220 }}>{'Message'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rowsPage.content.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <TableCellContent>{row.rowNumber}</TableCellContent>
                          </TableCell>
                          <TableCell>
                            <TableCellContent>{row.word}</TableCellContent>
                          </TableCell>
                          <TableCell>
                            <TableCellContent>
                              {row.partOfSpeech || ''}
                            </TableCellContent>
                          </TableCell>
                          <TableCell>
                            <TableCellContent>
                              {row.translation || ''}
                            </TableCellContent>
                          </TableCell>
                          <TableCell>
                            <TableCellContent>
                              {row.definition || ''}
                            </TableCellContent>
                          </TableCell>
                          <TableCell>
                            <TableCellContent>
                              {row.learned ? 'yes' : 'no'}
                            </TableCellContent>
                          </TableCell>
                          <TableCell>
                            <TableCellContent>
                              {row.activeSpeech ? 'yes' : 'no'}
                            </TableCellContent>
                          </TableCell>
                          <TableCell>
                            <TableCellContent>
                              {statusLabels[row.status]}
                            </TableCellContent>
                          </TableCell>
                          <TableCell>
                            <TableCellContent>
                              {row.errorMessage || ''}
                            </TableCellContent>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <HistoryPagination
                  currentPage={page + 1}
                  onChange={(nextPage) => setPage(nextPage - 1)}
                  totalPages={rowsPage.totalPages}
                />
              </Stack>
            </CardContent>
          </Card>
        ) : rowsLoading ? (
          <Alert severity={'info'}>{'Завантаження preview...'}</Alert>
        ) : null}
      </Stack>
    </UIContainer>
  );
};

export default VocabularyImport;
